import PropTypes from 'prop-types'
import React, { PureComponent } from 'react'
import { v2blue400 } from '@paypalcorp/pp-react'
import glamorous from 'glamorous'
import { isFontLoaded } from '../../lib/is-font-loaded'
import * as amountUtils from '../../lib/amountUtils'
import { moveCursorToTheEnd } from '../../lib/dom-utils'
import { MAX_AMOUNT_LENGTH } from 'transfer/constants'
import { isNaN } from 'lodash'
import {
  getAmountParts,
  sortCurrencyList,
} from '../../transfer/utils/currencyUtils'
import { colors } from '../../lib/styles/variables'
import { getCurrencyPrecision } from '../../../../lib/utils/currencyUtils'
import { isDw2Repaint } from 'experiments/dw2Repaint'

import content from 'pp-react-l10n'

let i18n = content('transfer/page/preview')

const INPUT_LINE_HEIGHT = 1.4
const SELECT_FONT_SIZE = isDw2Repaint() ? 18 : 13

const Label = glamorous.label({
  fontSize: 13,
  lineHeight: 1.62,
  color: '#7f868b',
  marginBottom: 14,
})

const InputWrapper = glamorous.div(
  {
    display: 'flex',
    justifyContent: 'center',
  },
  ({ isInvalid }) => {
    if (isInvalid) {
      return 'animated shake'
    }
  }
)

const Input = glamorous.input(
  {
    background: 'none',
    border: 'none',
    color: colors.inputText,
    textAlign: 'center',
    padding: '0',
    margin: '0',
    textOverflow: 'clip',
    outline: 0,
    cursor: 'pointer',
    letterSpacing: 'normal', // overrides vxpattern-lib-paypal letter-spacing value on all inputs
    lineHeight: INPUT_LINE_HEIGHT,
    fontFamily: isDw2Repaint()
      ? 'PayPalSansBig-Regular, "Helvetica Neue", Arial, sans-serif'
      : "'PayPal-Sans-Big', sans-serif",
    fontWeight: isDw2Repaint() ? 500 : 300,
  },
  ({ hideCursor, readOnly, isInvalid }) => {
    let style = {}
    if (hideCursor) {
      style = {
        ...style,
        color: 'transparent',
        textShadow: `${colors.inputText} 0 0 0`,
      }
    }
    if (readOnly) {
      style = {
        ...style,
        cursor: 'default',
      }
    }
    if (isInvalid) {
      style = {
        ...style,
        color: '#D20000',
      }
    }
    return style
  }
)

const SelectWrapper = glamorous.div(
  {
    textAlign: 'center',
    margin: '20px auto',
    color: isDw2Repaint() ? v2blue400 : '#0070ba',
    fontSize: SELECT_FONT_SIZE,
    position: 'relative',
    height: 32,
    // width: set dynamically based on width of selected option
  },
  ({ isCurrencyCodeReadOnly, selectColor }) => {
    if (selectColor) {
      return {
        color: selectColor,
      }
    }
    if (isCurrencyCodeReadOnly) {
      return {
        color: colors.inputText,
      }
    }
  },
  ({ isLoading }) => {
    if (isLoading) {
      return {
        opacity: 0.5,
      }
    }
  }
)

const Select = glamorous.select(
  {
    ...(!isDw2Repaint() && { position: 'absolute' }),
    fontWeight: isDw2Repaint() ? 600 : 500,
    appearance: 'none', // remove browser styles
    '::-ms-expand': { display: 'none' }, // remove browser styles for IE 11
    ...(!isDw2Repaint() && {
      paddingTop: 4,
      paddingRight: 18,
      paddingBottom: 4,
      paddingLeft: 10,
    }),
    backgroundColor: 'transparent',
    ...(!isDw2Repaint() && {
      top: 0,
      left: -14, // to compensate for the padding
    }),
    zIndex: 2,
    cursor: 'pointer',
    border: isDw2Repaint() ? 'none' : '1px #0070ba solid',
    ...(!isDw2Repaint() && { borderRadius: 32 }),
    ...(isDw2Repaint() && {
      fontFamily: 'PayPalSansBig-Regular, "Helvetica Neue", Arial, sans-serif',
    }),
  },
  ({ isCurrencyCodeReadOnly, hasMoreThanOneCurrency }) => {
    if (isCurrencyCodeReadOnly || !hasMoreThanOneCurrency) {
      return {
        cursor: 'default',
        color: '#3a3c3e',
        border: 0,
      }
    }
  },
  ({ disabled }) => {
    if (disabled) {
      return {
        cursor: 'default',
      }
    }
  }
)

const CurrencySymbol = glamorous.div(
  {
    marginRight: 4,
    marginTop: 10,
    textAlign: 'right',
    color: colors.inputText,
    fontSize: 18,
    fontWeight: 400,
    ...(isDw2Repaint() && {
      fontFamily: 'PayPalSansBig-Regular, "Helvetica Neue", Arial, sans-serif',
    }),
  },
  ({ isInvalid }) => {
    if (isInvalid) {
      return {
        color: '#D20000',
      }
    }
  }
)

const Arrow = glamorous.span('icon icon-arrow-down-small', {
  fontWeight: 'bold',
  position: 'absolute',
  top: 3,
  // left: set dynamically based on width of selected option
  zIndex: 1,
  ':before': {
    position: 'absolute',
    top: 10,
  },
})

export default class AmountField extends PureComponent {
  static propTypes = {
    name: PropTypes.string,
    dataNemo: PropTypes.string,
    id: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    currencyDecimal: PropTypes.string.isRequired,
    currencySymbolMap: PropTypes.object.isRequired,
    currencyCode: PropTypes.string.isRequired,
    currencyCodeList: PropTypes.array,
    value: PropTypes.string.isRequired,
    isAmountReadOnly: PropTypes.bool.isRequired,
    label: PropTypes.string,
    isCurrencyCodeReadOnly: PropTypes.bool,
    inputColor: PropTypes.string,
    selectColor: PropTypes.string,
    showInvalid: PropTypes.bool,
    focusAmountField: PropTypes.bool,
    onFocus: PropTypes.func,
    inputFontSize: PropTypes.number,
    isLoading: PropTypes.bool,
  }

  static defaultProps = {
    isAmountReadOnly: false,
    isCurrencyReadOnly: false,
  }

  state = {}

  constructor(props) {
    super(props)

    this.sortedCurrencyCodeList = sortCurrencyList(
      props.currencyCodeList,
      props.currencySymbolMap
    )
  }

  componentDidMount() {
    this._isMounted = true

    isFontLoaded('PayPal-Sans-Big')
      .then(() => {
        if (this._isMounted) {
          // it could have unmounted before this is resolved
          // once the font has been confirmed to be loaded, we need to recalculate the width of the input.
          this.forceUpdate()
        }
      })
      .catch(() => {
        // do absolutely nothing
      })

    if (!this.props.isAmountReadOnly && this._input) {
      this._input.focus()
    }
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  componentDidUpdate() {
    if (this.props.showInvalid) {
      this.showInvalid()
    }

    if (this.props.focusAmountField && this._input) {
      this._input.focus()
    }
  }

  /**
   * This handles the value and currency change. Because a change in currency can cause a change in the value (due to
   * differences in precision), this will be called when the value or the currency is changed.
   *
   * We first strip out all non-digits then validate that the result is a real number. If it's not, then we don't do
   * anything (and the input value is not updated). If the result is equal to 0, then we'll call onAmountChange with an
   * empty string. Otherwise, we'll format it and if it's not the same as it was before, we'll call onAmountChange with
   * that.
   * @param {Object} changes holds the new value and the currencyCode
   */
  handleChange({
    value = this.props.value,
    currencyCode = this.props.currencyCode,
  }) {
    const normalizedValue = amountUtils.normalizeNumberInputs(value)
    const cleanValue = amountUtils.replaceNonDigits(normalizedValue)

    // convert value to number to remove leading zeros
    const numberValue = Number(cleanValue)

    if (isNaN(numberValue) || cleanValue.length >= MAX_AMOUNT_LENGTH) {
      this.showInvalid()
      return
    } else if (numberValue === 0) {
      const newValue = ''
      this.props.onChange(newValue, currencyCode)
      return
    }

    const formattedValue = amountUtils.formatUserCentsInput(
      currencyCode,
      this.props.currencyDecimal,
      numberValue.toString()
    )
    if (
      formattedValue !== this.props.value ||
      currencyCode !== this.props.currencyCode
    ) {
      this.props.onChange(formattedValue, currencyCode)
    }
  }

  handleSelectChange = ({ target: { value: currencyCode } }) =>
    this.handleChange({ currencyCode })
  handleInputChange = ({ target: { value } }, inputFontSize) =>
    this.handleChange({ value, inputFontSize })
  handleFocus = ({ target }) => {
    moveCursorToTheEnd(target)
    if (this.props.onFocus) {
      this.props.onFocus(target)
    }
  }
  handleClick = ({ target }) => moveCursorToTheEnd(target)
  handleMouseDown = () => this.setState({ hideCursor: true })
  handleMouseUp = () => this.setState({ hideCursor: false })

  /**
   * This function sets the isInvalid state which results in a rerender which will add css classes to cause a shake
   * effect on the input wrapper. It also sets a timeout and sets isInvalid to false so the shake effect doesn't go on
   * forever.
   */
  showInvalid() {
    this.setState({ isInvalid: true }, () => {
      setTimeout(
        () =>
          this.setState({ isInvalid: false }, () => {
            if (this.props.focusAmountField) {
              this._input.focus()
            }
          }),
        300
      )
    })
  }

  render() {
    const { hideCursor, isInvalid } = this.state
    const {
      label,
      value,
      currencyCode,
      currencySymbolMap,
      currencyDecimal,
    } = this.props
    const currencySymbol = currencySymbolMap[currencyCode]

    // non-decimal currencies should have the digits after the decimal removed
    let adjustedValue = value
    if (getCurrencyPrecision(currencyCode) === 0) {
      adjustedValue = parseFloat(value || 0).toFixed(0)
    }

    const formattedValue = amountUtils.formatUserCentsInput(
      currencyCode,
      currencyDecimal,
      adjustedValue
    )
    const inputFontSize =
      this.props.inputFontSize ||
      amountUtils.updateFontSize(formattedValue, currencySymbol)
    const extraSpaceToAccountForWeirdChromeBug = 5
    const inputWidth =
      amountUtils.measureTextSizeWithStyle(
        inputFontSize,
        this._input,
        formattedValue
      ) + extraSpaceToAccountForWeirdChromeBug
    const selectWidth = amountUtils.measureTextSizeWithStyle(
      SELECT_FONT_SIZE,
      this._select,
      currencyCode
    )

    const amountComponents = {
      value: (
        <Input
          innerRef={c => (this._input = c)}
          aria-label={i18n('label.enterAmount')}
          key="amount_input"
          type="tel"
          dir="ltr"
          name={this.props.name}
          data-nemo={this.props.dataNemo}
          id={this.props.id}
          autoComplete="off"
          required
          readOnly={this.props.isAmountReadOnly}
          hideCursor={hideCursor}
          inputColor={this.props.inputColor}
          isInvalid={isInvalid}
          value={formattedValue}
          onChange={this.handleInputChange}
          onFocus={this.handleFocus}
          onClick={this.handleClick}
          onMouseDown={this.handleMouseDown}
          onMouseUp={this.handleMouseUp}
          css={{
            width: inputWidth,
            fontSize: inputFontSize,
          }}
        />
      ),
      symbol: (
        <CurrencySymbol
          key="currency_symbol"
          color={this.props.inputColor}
          isInvalid={isInvalid}
        >
          {currencySymbol}
        </CurrencySymbol>
      ),
    }

    // parts gives us an array of each individual elements of the formatted amount
    // e.g. [{ type: 'symbol', value: '$' }, { type: 'value', value: '1,000,000.00' }]
    const parts = getAmountParts(
      this.props.value,
      this.props.currencyCode,
      this.props.currencyDecimal
    )

    // since the currency code is fixed below the amount field, we care only about
    // the symbol and value parts
    const amountPartsValueAndSymbol = parts.filter(
      part => part.type === 'symbol' || part.type === 'value'
    )

    // create an array of the amount field and currency symbol, in the order that
    // is appropriate based on the `parts()` array
    const amountFieldAndSymbol = amountPartsValueAndSymbol.map(
      part => amountComponents[part.type]
    )

    const hasMoreThanOneCurrency = this.sortedCurrencyCodeList.length > 1

    const showSpinner =
      this.props.isLoading && document.activeElement !== this._input
    const shouldDisableSelect =
      this.props.isCurrencyCodeReadOnly ||
      !hasMoreThanOneCurrency ||
      this.props.isLoading

    const defaultCurrency = this.props.currencyCode

    return (
      <div>
        {label && <Label>{label}</Label>}
        <InputWrapper dir="ltr" isInvalid={isInvalid}>
          {showSpinner ? (
            <div
              // make spin icon same height with input
              // input height = inputFontSize * INPUT_LINE_HEIGHT
              // spin height = height + 2 * margin
              style={{
                width: inputFontSize,
                height: inputFontSize,
                margin: ((INPUT_LINE_HEIGHT - 1) * inputFontSize) / 2,
              }}
              className="vx_spinner-small"
            />
          ) : (
            amountFieldAndSymbol
          )}
        </InputWrapper>
        <SelectWrapper
          isLoading={this.props.isLoading}
          isCurrencyCodeReadOnly={this.props.isCurrencyCodeReadOnly}
          selectColor={this.props.selectColor}
          css={{
            width: selectWidth,
          }}
        >
          <Select
            name="currencies"
            data-nemo="currencies"
            isCurrencyCodeReadOnly={this.props.isCurrencyCodeReadOnly}
            hasMoreThanOneCurrency={hasMoreThanOneCurrency}
            value={defaultCurrency}
            disabled={shouldDisableSelect}
            onChange={this.handleSelectChange}
            innerRef={c => (this._select = c)}
          >
            {this.sortedCurrencyCodeList.map(code => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>

          {!isDw2Repaint() &&
            !this.props.isCurrencyCodeReadOnly &&
            hasMoreThanOneCurrency && (
              // should move left by 4 ((select padding right - select padding left)/2), then leave 2px as margin
              <Arrow css={{ left: selectWidth - 2 }} />
            )}
        </SelectWrapper>
      </div>
    )
  }
}
